import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Package,
  Plus,
  Minus,
  Check,
  X,
  CreditCard,
  Banknote,
  Smartphone,
  ArrowRightLeft,
  RefreshCw,
  Camera,
  FileText,
  Truck,
  Scan,
  Trash2,
  AlertCircle,
  ArrowLeft,
  Flashlight,
  Search,
  Pencil,
  ChevronRight
} from 'lucide-react';

// ==========================================
// SOUND HELPERS
// ==========================================
const playBeep = (freq = 880, ms = 100) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(freq, ctx.currentTime);
    g.gain.setValueAtTime(0.3, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + ms / 1000);
    o.connect(g);
    g.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + ms / 1000);
  } catch (e) { console.debug('Audio beep unavailable:', e); }
};

const playErrorBeep = () => {
  playBeep(300, 200);
  setTimeout(() => playBeep(200, 300), 220);
};

// ==========================================
// FULLSCREEN SCANNER COMPONENT
// Native BarcodeDetector (Android Chrome — fast, accurate) with html5-qrcode
// fallback. High-res camera, continuous focus, torch and zoom controls.
// ==========================================
const SCAN_FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'qr_code'];

function FullscreenScanner({ onScan, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const intervalRef = useRef(null);
  const html5Ref = useRef(null);
  const lockRef = useRef(false);
  const lastCodeRef = useRef('');
  const lastTimeRef = useRef(0);
  const [engine, setEngine] = useState(null); // 'native' | 'fallback'
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [zoomCaps, setZoomCaps] = useState(null);
  const [zoom, setZoom] = useState(1);
  const COOLDOWN = 2000;
  const containerId = 'fullscreen-barcode-scanner';

  const handleDetected = (decodedText) => {
    const now = Date.now();
    if (lockRef.current) return;
    if (decodedText === lastCodeRef.current && (now - lastTimeRef.current) < COOLDOWN) return;
    lockRef.current = true;
    lastCodeRef.current = decodedText;
    lastTimeRef.current = now;
    playBeep(880, 100);
    if (navigator.vibrate) navigator.vibrate(80);
    onScan(decodedText);
    setTimeout(() => { lockRef.current = false; }, COOLDOWN);
  };

  useEffect(() => {
    let cancelled = false;

    const startNative = async () => {
      if (!('BarcodeDetector' in window)) return false;
      try {
        const supported = await window.BarcodeDetector.getSupportedFormats();
        const formats = SCAN_FORMATS.filter((f) => supported.includes(f));
        if (!formats.includes('ean_13')) return false;
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 2560 },
            height: { ideal: 1440 },
          },
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return true; }
        streamRef.current = stream;
        const track = stream.getVideoTracks()[0];
        try { await track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }); } catch { /* Focus constraint not supported */ }
        const caps = track.getCapabilities ? track.getCapabilities() : {};
        setTorchSupported(!!caps.torch);
        if (caps.zoom && caps.zoom.max > 1) setZoomCaps(caps.zoom);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        detectorRef.current = new window.BarcodeDetector({ formats });
        intervalRef.current = setInterval(async () => {
          const v = videoRef.current;
          if (lockRef.current || !v || v.readyState < 2) return;
          try {
            // 1st pass: crop to the central band (where the guide frame is) —
            // smaller region = bigger relative barcode = much better EAN-13 reads
            const vw = v.videoWidth, vh = v.videoHeight;
            let codes = [];
            if (vw > 0 && typeof createImageBitmap === 'function') {
              const cw = Math.floor(vw * 0.85);
              const ch = Math.floor(vh * 0.4);
              const bmp = await createImageBitmap(v, Math.floor((vw - cw) / 2), Math.floor((vh - ch) / 2), cw, ch);
              codes = await detectorRef.current.detect(bmp);
              bmp.close();
            }
            // 2nd pass: full frame (catches codes held off-centre)
            if (codes.length === 0) {
              codes = await detectorRef.current.detect(v);
            }
            if (codes.length > 0 && codes[0].rawValue) handleDetected(codes[0].rawValue);
          } catch (e) { /* erros de descodificação por frame são esperados ao ritmo de scan */ }
        }, 70); // ~14 scans/s
        setEngine('native');
        return true;
      } catch (err) {
        console.error('Native scanner error:', err);
        return false;
      }
    };

    const startFallback = async () => {
      try {
        const scanner = new Html5Qrcode(containerId, {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.QR_CODE,
          ],
        });
        html5Ref.current = scanner;
        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 15,
            qrbox: (w, h) => ({ width: Math.min(w * 0.85, 350), height: Math.min(h * 0.3, 160) }),
            disableFlip: true,
            videoConstraints: {
              facingMode: 'environment',
              width: { ideal: 1920 },
              height: { ideal: 1080 },
            },
          },
          (decodedText) => handleDetected(decodedText),
          () => {}
        );
        setEngine('fallback');
      } catch (err) {
        console.error('Camera error:', err);
        toast.error('Erro ao iniciar câmara. Verifique as permissões.');
        onClose();
      }
    };

    (async () => {
      const ok = await startNative();
      if (!ok && !cancelled) await startFallback();
    })();

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (html5Ref.current) {
        try { html5Ref.current.stop().catch(() => {}); } catch (e) { console.debug('html5-qrcode stop failed:', e); }
        try { html5Ref.current.clear(); } catch (e) { console.debug('html5-qrcode clear failed:', e); }
        html5Ref.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn(!torchOn);
    } catch (e) {
      toast.error('Lanterna não suportada neste dispositivo');
    }
  };

  const applyZoom = async (level) => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track || !zoomCaps) return;
    const value = Math.min(Math.max(level, zoomCaps.min || 1), zoomCaps.max);
    try {
      await track.applyConstraints({ advanced: [{ zoom: value }] });
      setZoom(level);
    } catch (e) { console.debug('zoom not supported:', e); }
  };

  const zoomLevels = zoomCaps
    ? [1, 2, zoomCaps.max >= 3 ? 3 : null].filter((z) => z && z <= zoomCaps.max)
    : [];

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col" data-testid="fullscreen-scanner">
      {/* Camera */}
      <div className="flex-1 relative overflow-hidden">
        {engine !== 'fallback' && (
          <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" playsInline muted />
        )}
        <div id={containerId} className={engine === 'fallback' ? 'absolute inset-0 w-full h-full' : 'hidden'} />

        {/* Scan guide overlay (native engine) */}
        {engine === 'native' && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-[85%] max-w-[350px] h-32 border-2 border-white/70 rounded-lg relative">
              <div className="absolute left-2 right-2 top-1/2 h-0.5 bg-red-500/80 rounded animate-pulse" />
            </div>
          </div>
        )}

        {/* Top controls: torch + zoom */}
        <div className="absolute top-[max(1rem,env(safe-area-inset-top))] left-0 right-0 px-4 flex items-center justify-between pointer-events-none">
          <div className="flex gap-2 pointer-events-auto">
            {zoomLevels.length > 0 && zoomLevels.map((z) => (
              <button
                key={z}
                onClick={() => applyZoom(z)}
                className={`h-11 w-11 rounded-full text-sm font-bold flex items-center justify-center ${
                  zoom === z ? 'bg-white text-black' : 'bg-black/50 text-white border border-white/40'
                }`}
                data-testid={`scanner-zoom-${z}x`}
              >
                {z}x
              </button>
            ))}
          </div>
          {torchSupported && (
            <button
              onClick={toggleTorch}
              className={`h-11 w-11 rounded-full flex items-center justify-center pointer-events-auto ${
                torchOn ? 'bg-amber-400 text-black' : 'bg-black/50 text-white border border-white/40'
              }`}
              data-testid="scanner-torch-btn"
            >
              <Flashlight className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Bottom controls */}
      <div className="bg-black/90 px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <p className="text-white/60 text-xs text-center mb-3">
          Aponte a câmara para o código de barras
        </p>
        <button
          onClick={onClose}
          className="w-full h-14 rounded-xl bg-white text-black font-semibold text-base flex items-center justify-center gap-2 active:bg-slate-200"
          data-testid="close-scanner-btn"
        >
          <ArrowLeft className="h-5 w-5" />
          Voltar aos Serviços
        </button>
      </div>
    </div>
  );
}

// ==========================================
// MAIN COMPONENT
// ==========================================
export default function TechConsumption() {
  const { user } = useContext(AuthContext);

  // Core state
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [apNumber, setApNumber] = useState('');
  const [currentRecord, setCurrentRecord] = useState(null);
  const [manualSku, setManualSku] = useState('');
  const [loading, setLoading] = useState(false);
  const [recentRecords, setRecentRecords] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Scanner state — fullscreen overlay
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanFeedback, setScanFeedback] = useState(null);

  // Close AP modal
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('paid');
  const [wantsInvoice, setWantsInvoice] = useState(null);
  const [clientNif, setClientNif] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [laborValue, setLaborValue] = useState('');
  const [travelValue, setTravelValue] = useState('');
  const [doorValue, setDoorValue] = useState('');
  const [otherValue, setOtherValue] = useState('');
  const [otherDescription, setOtherDescription] = useState('');

  // Ref for scanner callback (avoids stale closure)
  const currentRecordRef = useRef(null);
  useEffect(() => { currentRecordRef.current = currentRecord; }, [currentRecord]);

  // ==========================================
  // DATA FETCHING
  // ==========================================
  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/warehouses`);
      let allWarehouses = response.data.warehouses || [];
      // Get user's reference warehouse (canonical) with legacy fallback
      const userRefWh = user?.reference_warehouse || user?.default_warehouse;
      // Técnicos só veem a(s) sua(s) carrinha(s) — nunca as dos outros
      if (user?.role === 'technician') {
        const allowed = new Set(
          [...(user.allowed_warehouses || []), userRefWh].filter(Boolean)
        );
        if (allowed.size > 0) {
          allWarehouses = allWarehouses.filter(w => allowed.has(w.code));
        }
      }
      setWarehouses(allWarehouses);
      if (!selectedWarehouse) {
        if (userRefWh) {
          setSelectedWarehouse(userRefWh);
        } else if (user?.role === 'technician') {
          const vans = allWarehouses.filter(w => w.type === 'van');
          if (vans.length > 0) setSelectedWarehouse(vans[0].code);
        }
      }
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
  }, [selectedWarehouse, user]);

  const fetchRecentRecords = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/consumptions`, { params: { page_size: 10 } });
      setRecentRecords(response.data.items || []);
    } catch (error) {
      console.error('Error fetching recent records:', error);
    }
  }, []);

  useEffect(() => {
    fetchWarehouses();
    fetchRecentRecords();
  }, [fetchWarehouses, fetchRecentRecords]);

  // ==========================================
  // RECORD MANAGEMENT
  // ==========================================
  
  // Helper to reset all close-form state (service values, payment, invoice)
  const resetCloseFormState = () => {
    setPaymentMethod('');
    setPaymentStatus('paid');
    setWantsInvoice(null);
    setClientNif('');
    setClientName('');
    setClientEmail('');
    setLaborValue('');
    setTravelValue('');
    setDoorValue('');
    setOtherValue('');
    setOtherDescription('');
  };

  const createRecord = async () => {
    if (!apNumber.trim()) { toast.error('Indique o número do AP'); return; }
    if (!selectedWarehouse) { toast.error('Seleccione a carrinha'); return; }
    setLoading(true);
    // Reset service/payment state before creating new AP
    resetCloseFormState();
    try {
      const response = await axios.post(`${API}/consumptions`, {
        ap_number: apNumber.trim(),
        warehouse_code: selectedWarehouse,
        items: []
      });
      setCurrentRecord(response.data);
      toast.success(`AP ${apNumber} criado`);
    } catch (error) {
      toast.error('Erro ao criar registo');
    } finally {
      setLoading(false);
    }
  };

  // Add item — called from scanner or manual input
  const addItemByBarcode = useCallback(async (barcode) => {
    const record = currentRecordRef.current;
    if (!record) {
      toast.error('Crie primeiro um AP');
      return;
    }

    // Proactive stock check — resolve the barcode and read van stock BEFORE
    // attempting to add. Gives instant "Stock: N" feedback (UX) and prevents
    // a reactive HTTP 400 round-trip. Fail-gracefully: if the lookup itself
    // fails, fall back to the backend add which has the same guard.
    let preview = null;
    try {
      const r = await axios.get(`${API}/products/by-barcode/${encodeURIComponent(barcode)}`);
      preview = r.data;
    } catch (e) { console.debug('barcode preview lookup failed, falling through to add:', e); }

    const wh = record.warehouse_code;
    if (preview && wh) {
      const sbw = preview.stock_by_warehouse || {};
      const reservedOthers = Number((preview.reserved_by_warehouse || {})[wh] || 0);
      const available = Number(sbw[wh] || 0) - reservedOthers;
      const alreadyInAp = (record.items || []).find(i => i.sku === preview.sku)?.quantity || 0;
      if (available < 1) {
        playErrorBeep();
        const pname = preview.name ? (preview.name.length > 36 ? `${preview.name.slice(0, 36)}…` : preview.name) : preview.sku;
        const cativoNote = reservedOthers > 0 ? ` · ${reservedOthers} cativo em APs` : '';
        setScanFeedback({
          type: 'error',
          text: `${pname} · sem stock disponível (${Math.max(0, available)} un.${cativoNote})`,
        });
        setTimeout(() => setScanFeedback(null), 3500);
        toast.error(
          `${preview.sku}: sem stock disponível na carrinha (${Math.max(0, available)} un.${reservedOthers > 0 ? `, ${reservedOthers} cativas em APs não faturados` : ''}, já tens ${alreadyInAp} no AP). Pede reposição via TAR.`,
          { duration: 5000 },
        );
        return;
      }
    }

    try {
      const response = await axios.post(
        `${API}/consumptions/${record.id}/add-item`,
        null,
        { params: { sku: barcode, quantity: 1 } }
      );
      const updated = await axios.get(`${API}/consumptions/${record.id}`);
      setCurrentRecord(updated.data);
      const itemSku = response.data.item_added || barcode;
      const addedItem = (updated.data.items || []).find(i => i.sku === itemSku);
      const displayName = addedItem?.name || preview?.name || itemSku;
      const shortName = displayName.length > 42 ? `${displayName.slice(0, 42)}…` : displayName;
      // Show remaining AVAILABLE stock after add (physical − reserved in APs − this add)
      const remaining = preview && wh
        ? Number((preview.stock_by_warehouse || {})[wh] || 0)
          - Number((preview.reserved_by_warehouse || {})[wh] || 0) - 1
        : null;
      setScanFeedback({
        type: 'success',
        text: remaining !== null
          ? `+1 ${shortName} · resta ${remaining} un.`
          : `+1 ${shortName}`,
      });
      setTimeout(() => setScanFeedback(null), 2500);
    } catch (error) {
      playErrorBeep();
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 404) {
        setScanFeedback({ type: 'error', text: `Não encontrado: ${barcode}` });
      } else if (error.response?.status === 400 && detail) {
        setScanFeedback({ type: 'error', text: detail.slice(0, 80) });
        toast.error(detail, { duration: 5000 });
      } else {
        setScanFeedback({ type: 'error', text: 'Erro ao adicionar' });
      }
      setTimeout(() => setScanFeedback(null), 3500);
    }
  }, []);

  const handleManualAdd = async () => {
    if (!manualSku.trim()) return;
    await addItemByBarcode(manualSku.trim());
    setManualSku('');
    setSearchResults([]);
  };

  // Debounced search of van stock (name / SKU / barcode)
  useEffect(() => {
    if (!currentRecord?.warehouse_code) return;
    const q = manualSku.trim();
    if (q.length < 2) { setSearchResults([]); setSearchLoading(false); return; }
    setSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/warehouses/${currentRecord.warehouse_code}/products`, {
          params: { search: q, has_stock: true, page_size: 8 },
        });
        setSearchResults(res.data.products || []);
      } catch (e) {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualSku, currentRecord?.warehouse_code]);

  const handleAddFromSearch = async (sku) => {
    await addItemByBarcode(sku);
    setManualSku('');
    setSearchResults([]);
  };

  const updateItemQuantity = async (sku, delta) => {
    if (!currentRecord) return;
    const item = currentRecord.items.find(i => i.sku === sku);
    if (!item) return;
    const newQty = item.quantity + delta;

    if (newQty <= 0) {
      try {
        await axios.delete(`${API}/consumptions/${currentRecord.id}/items/${sku}`);
        const updated = await axios.get(`${API}/consumptions/${currentRecord.id}`);
        setCurrentRecord(updated.data);
        toast.success('Item removido');
      } catch (error) { toast.error('Erro ao remover item'); }
    } else {
      try {
        await axios.post(`${API}/consumptions/${currentRecord.id}/add-item`, null, { params: { sku, quantity: delta } });
        const updated = await axios.get(`${API}/consumptions/${currentRecord.id}`);
        setCurrentRecord(updated.data);
      } catch (error) {
        const detail = error.response?.data?.detail;
        toast.error(detail || 'Erro ao actualizar quantidade', { duration: 5000 });
      }
    }
  };

  const handleCloseRecord = async () => {
    if (paymentStatus === 'paid' && !paymentMethod) {
      toast.error('Seleciona o método de pagamento (ou marca como Não Pago/Pendente)');
      return;
    }
    if (wantsInvoice === null) { toast.error('Indica se o cliente pretende fatura'); return; }
    if (wantsInvoice === true) {
      if (!/^\d{9}$/.test(clientNif)) { toast.error('NIF inválido — indica 9 dígitos'); return; }
      if (clientName.trim().length < 3) { toast.error('Indica o nome para a fatura'); return; }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clientEmail.trim())) { toast.error('Email inválido para envio da fatura'); return; }
    }
    const labor = parseFloat(laborValue) || 0;
    const travel = parseFloat(travelValue) || 0;
    const door = parseFloat(doorValue) || 0;
    const other = parseFloat(otherValue) || 0;
    if (other > 0 && !otherDescription.trim()) {
      toast.error('Indica uma descrição para o valor "Outros"');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/consumptions/${currentRecord.id}/close`, {
        payment_method: paymentMethod || null,
        payment_status: paymentStatus,
        labor_value: labor,
        travel_value: travel,
        door_value: door,
        other_value: other,
        other_description: otherDescription.trim() || null,
        wants_invoice: wantsInvoice,
        client_nif: wantsInvoice ? clientNif : null,
        client_name: wantsInvoice ? clientName.trim() : null,
        client_email: wantsInvoice ? clientEmail.trim() : null,
      });
      if (res.data?.sage_din_reference) {
        toast.success(`AP ${currentRecord.ap_number} fechado · ${res.data.sage_din_reference} criado no SAGE`);
      } else {
        toast.success(`AP ${currentRecord.ap_number} fechado`);
        if (res.data?.sage_din_error) {
          toast.warning('DIN não criado no SAGE — o escritório pode re-tentar na Faturação', { duration: 6000 });
        }
      }
      setShowCloseModal(false);
      setCurrentRecord(null);
      setApNumber('');
      setPaymentMethod('');
      setPaymentStatus('paid');
      setWantsInvoice(null);
      setClientNif('');
      setClientName('');
      setClientEmail('');
      setLaborValue('');
      setDoorValue('');
      setTravelValue('');
      setOtherValue('');
      setOtherDescription('');
      setScannerOpen(false);
      fetchRecentRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fechar AP');
    }
    finally { setLoading(false); }
  };

  const handleDeleteOpenAP = async (record) => {
    if (!window.confirm(`Eliminar AP ${record.ap_number}? Esta acção não pode ser desfeita.`)) return;
    try {
      await axios.delete(`${API}/consumptions/${record.id}`);
      toast.success(`AP ${record.ap_number} eliminado`);
      if (currentRecord?.id === record.id) {
        setCurrentRecord(null);
        setApNumber('');
      }
      fetchRecentRecords();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao eliminar AP');
    }
  };

  const handleCancelRecord = () => {
    setCurrentRecord(null);
    setApNumber('');
    setScannerOpen(false);
    // Reset service/payment/invoice state to avoid leaking into next AP
    resetCloseFormState();
  };

  const handleReopenAP = async (record) => {
    if (!window.confirm(`Reabrir AP ${record.ap_number} para consulta/correção? Terás de o fechar novamente no fim.`)) return;
    try {
      const res = await axios.post(`${API}/consumptions/${record.id}/reopen`);
      setCurrentRecord(res.data);
      setApNumber(res.data.ap_number || '');
      setSelectedWarehouse(res.data.warehouse_code || selectedWarehouse);
      // Pre-fill close data from previous close for convenience
      setPaymentMethod(res.data.payment_method || '');
      setPaymentStatus(res.data.payment_status && res.data.payment_status !== 'pending' ? res.data.payment_status : 'paid');
      setWantsInvoice(res.data.wants_invoice ?? null);
      setClientNif(res.data.client_nif || '');
      setClientName(res.data.client_name || '');
      setClientEmail(res.data.client_email || '');
      setLaborValue(res.data.labor_value ? String(res.data.labor_value) : '');
      setTravelValue(res.data.travel_value ? String(res.data.travel_value) : '');
      setDoorValue(res.data.door_value ? String(res.data.door_value) : '');
      setOtherValue(res.data.other_value ? String(res.data.other_value) : '');
      setOtherDescription(res.data.other_description || '');
      toast.success(`AP ${res.data.ap_number} reaberto — corrige e volta a fechar`);
      fetchRecentRecords();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao reabrir AP');
    }
  };

  // Scanner callback — stable ref
  const handleScanFromScanner = useCallback((barcode) => {
    addItemByBarcode(barcode);
  }, [addItemByBarcode]);

  const selectedWarehouseName = warehouses.find(w => w.code === selectedWarehouse)?.name || selectedWarehouse;

  // ==========================================
  // RENDER
  // ==========================================

  // FULLSCREEN SCANNER OVERLAY
  if (scannerOpen && currentRecord) {
    return (
      <>
        <FullscreenScanner
          onScan={handleScanFromScanner}
          onClose={() => setScannerOpen(false)}
        />
        {/* Feedback toast over scanner */}
        {scanFeedback && (
          <div className="fixed top-16 left-4 right-4 z-[110]" data-testid="scan-feedback-overlay">
            <div className={`rounded-xl px-4 py-3 shadow-lg flex items-center gap-2 text-white font-semibold ${
              scanFeedback.type === 'success' ? 'bg-green-600' : 'bg-red-600'
            }`}>
              {scanFeedback.type === 'success'
                ? <Check className="h-5 w-5 flex-shrink-0" />
                : <AlertCircle className="h-5 w-5 flex-shrink-0" />}
              <span className="text-sm">{scanFeedback.text}</span>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="min-h-[100dvh] flex flex-col bg-white" data-testid="tech-consumption-page">

      {/* ====== NO ACTIVE RECORD ====== */}
      {!currentRecord ? (
        <div className="flex-1 p-4 pb-24">
          {/* Header - Compact */}
          <div className="mb-6">
            <h1 className="text-xl font-semibold text-slate-900" data-testid="consumption-title">Serviços</h1>
            <p className="text-sm text-slate-500 mt-0.5">Registo de artigos utilizados em AP</p>
          </div>

          {/* Form - Direct, no decorative container */}
          <div className="space-y-4 mb-8">
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Número do AP</label>
              <Input
                placeholder="AP-1234"
                value={apNumber}
                onChange={(e) => setApNumber(e.target.value)}
                className="h-12 text-base mt-1 border-slate-300 bg-slate-50 focus:bg-white"
                data-testid="ap-number-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Carrinha</label>
              <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
                <SelectTrigger className="h-12 mt-1 border-slate-300 bg-slate-50" data-testid="warehouse-select">
                  <SelectValue placeholder="Selecionar carrinha..." />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((wh) => (
                    <SelectItem key={wh.code} value={wh.code}>
                      <div className="flex items-center gap-2">
                        {wh.type === 'van' ? <Truck className="h-4 w-4 text-slate-400" /> : <Package className="h-4 w-4 text-slate-400" />}
                        <span>{wh.name}</span>
                        <span className="text-slate-400 text-sm">({wh.code})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={createRecord}
              className="w-full h-14 text-base font-medium text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#C41E3A' }}
              disabled={loading || !apNumber.trim() || !selectedWarehouse}
              data-testid="start-record-btn"
            >
              {loading ? <RefreshCw className="h-5 w-5 animate-spin mr-2" /> : null}
              Iniciar serviço
            </Button>
          </div>

          {/* Recent Records - Quiet design */}
          {recentRecords.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-3">Recentes</p>
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 bg-white">
                {recentRecords.slice(0, 5).map((record) => {
                  const isOpen = record.status === 'open';
                  return (
                    <div
                      key={record.id}
                      className={`flex items-center justify-between p-3 ${
                        isOpen ? 'hover:bg-slate-50' : 'opacity-60'
                      }`}
                    >
                      <button
                        onClick={async () => {
                          if (!isOpen) {
                            toast.info('Este serviço já está fechado.');
                            return;
                          }
                          try {
                            const fresh = await axios.get(`${API}/consumptions/${record.id}`);
                            setCurrentRecord(fresh.data);
                            setApNumber(fresh.data.ap_number || '');
                            setSelectedWarehouse(fresh.data.warehouse_code || selectedWarehouse);
                            toast.success(`Reaberto ${fresh.data.ap_number}`);
                          } catch (e) {
                            toast.error('Erro ao reabrir o registo');
                          }
                        }}
                        disabled={!isOpen}
                        data-testid={`recent-record-${record.ap_number}`}
                        className="flex-1 text-left flex items-center justify-between"
                        title={isOpen ? 'Clica para continuar este serviço' : 'Serviço fechado'}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm text-slate-900">{record.ap_number}</div>
                          <div className="text-xs text-slate-500">{record.warehouse_code} · {record.items?.length || 0} artigos</div>
                        </div>
                        <div className="text-right flex items-center gap-3">
                          <span className="font-mono text-sm font-medium text-slate-700">€{(record.total_value || 0).toFixed(2)}</span>
                          {isOpen ? (
                            <ChevronRight className="h-4 w-4 text-slate-400" />
                          ) : (
                            <span className="text-xs text-slate-400">{record.payment_method || 'Fechado'}</span>
                          )}
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* ====== ACTIVE RECORD — ITEMS VIEW ====== */
        <div className="flex-1 flex flex-col pb-[140px]">
          {/* Record Header */}
          <div className="sticky top-0 z-20 bg-white border-b px-4 py-2.5 flex items-center justify-between shadow-sm"
               data-testid="active-record-header">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-2.5 w-2.5 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
              <div className="min-w-0">
                <p className="font-bold text-sm truncate">{currentRecord.ap_number}</p>
                <p className="text-xs text-slate-500 truncate">{currentRecord.warehouse_name || selectedWarehouseName}</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Badge variant="secondary" className="font-mono text-xs px-2 py-0.5">
                {currentRecord.items?.length || 0}
              </Badge>
              <Badge className="font-mono text-xs bg-slate-800 text-white px-2 py-0.5">
                €{((currentRecord.total_value || 0) + (parseFloat(laborValue) || 0) + (parseFloat(travelValue) || 0) + (parseFloat(doorValue) || 0) + (parseFloat(otherValue) || 0)).toFixed(2)}
              </Badge>
            </div>
          </div>

          {/* Scan Feedback Banner (when returning from scanner) */}
          {scanFeedback && (
            <div className={`mx-4 mt-3 px-3 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 ${
              scanFeedback.type === 'success'
                ? 'bg-green-100 text-green-800 border border-green-200'
                : 'bg-red-100 text-red-800 border border-red-200'
            }`} data-testid="scan-feedback-banner">
              {scanFeedback.type === 'success' ? <Check className="h-4 w-4 flex-shrink-0" /> : <AlertCircle className="h-4 w-4 flex-shrink-0" />}
              {scanFeedback.text}
            </div>
          )}

          {/* SCAN BUTTON — Opens fullscreen scanner */}
          <div className="px-4 pt-3">
            <button
              onClick={() => setScannerOpen(true)}
              className="w-full h-16 rounded-xl bg-slate-800 active:bg-slate-700 text-white flex items-center justify-center gap-3 shadow-md"
              data-testid="open-scanner-btn"
            >
              <Camera className="h-6 w-6" />
              <span className="font-semibold">Ler Código de Barras</span>
            </button>
          </div>

          {/* Van stock search — always visible */}
          <div className="px-4 pt-2">
            <div className="mt-1">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  <Input
                    placeholder="Procurar na carrinha (nome, SKU, código)..."
                    value={manualSku}
                    onChange={(e) => setManualSku(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleManualAdd()}
                    className="h-12 text-base pl-9"
                    data-testid="manual-sku-input"
                  />
                </div>
                <Button
                  onClick={handleManualAdd}
                  disabled={!manualSku.trim() || loading}
                  className="h-12 w-14 bg-red-600 hover:bg-red-700 active:bg-red-800"
                  data-testid="add-item-btn"
                >
                  <Plus className="h-5 w-5" />
                </Button>
              </div>

                {/* Search results from van stock */}
                {searchLoading && (
                  <div className="flex items-center gap-2 text-xs text-slate-400 mt-2 px-1">
                    <RefreshCw className="h-3 w-3 animate-spin" /> A procurar na carrinha...
                  </div>
                )}
                {!searchLoading && manualSku.trim().length >= 2 && searchResults.length === 0 && (
                  <p className="text-xs text-slate-400 mt-2 px-1" data-testid="search-no-results">
                    Nenhum artigo com stock na carrinha para &quot;{manualSku.trim()}&quot;
                  </p>
                )}
                {searchResults.length > 0 && (
                  <div className="mt-2 bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden shadow-sm" data-testid="van-search-results">
                    {searchResults.map((p) => {
                      const available = p.available !== undefined ? p.available : (p.warehouse_stock || 0);
                      const reserved = p.reserved || 0;
                      return (
                        <div key={p.sku} className="flex items-center gap-2 p-2.5" data-testid={`search-result-${p.sku}`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 leading-snug">{p.name}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[11px] font-mono text-slate-400">{p.sku}</span>
                              <span className={`text-[11px] font-semibold ${available > 0 ? 'text-green-600' : 'text-red-500'}`}>
                                {available} disp.
                              </span>
                              {reserved > 0 && (
                                <span className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-px whitespace-nowrap">
                                  {reserved} cativo
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleAddFromSearch(p.sku)}
                            disabled={loading || available < 1}
                            className="h-11 w-11 rounded-lg bg-red-600 active:bg-red-700 disabled:opacity-35 text-white flex items-center justify-center flex-shrink-0"
                            data-testid={`search-add-${p.sku}`}
                          >
                            <Plus className="h-5 w-5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
            </div>
          </div>

          {/* VALUES SECTION — labor / travel / door / other — moved here for UX */}
          <div className="px-4 pt-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-3 space-y-3">
              <div className="text-sm font-medium text-slate-700">Serviços (opcional)</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs text-slate-600">Mão de Obra (€)</label>
                  <Input
                    type="number" inputMode="decimal" step="0.01" min="0"
                    value={laborValue}
                    onChange={(e) => setLaborValue(e.target.value)}
                    placeholder="0.00"
                    className="h-11 bg-white"
                    data-testid="labor-input-main"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-600">Deslocação (€)</label>
                  <Input
                    type="number" inputMode="decimal" step="0.01" min="0"
                    value={travelValue}
                    onChange={(e) => setTravelValue(e.target.value)}
                    placeholder="0.00"
                    className="h-11 bg-white"
                    data-testid="travel-input-main"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs text-slate-600">Abertura de porta (€)</label>
                  <Input
                    type="number" inputMode="decimal" step="0.01" min="0"
                    value={doorValue}
                    onChange={(e) => setDoorValue(e.target.value)}
                    placeholder="0.00"
                    className="h-11 bg-white"
                    data-testid="door-input-main"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-600">Outros (€)</label>
                  <Input
                    type="number" inputMode="decimal" step="0.01" min="0"
                    value={otherValue}
                    onChange={(e) => setOtherValue(e.target.value)}
                    placeholder="0.00"
                    className="h-11 bg-white"
                    data-testid="other-input-main"
                  />
                </div>
              </div>
              {parseFloat(otherValue) > 0 && (
                <div className="space-y-1">
                  <label className="text-xs text-slate-600">Descrição &quot;Outros&quot; <span className="text-red-500">*</span></label>
                  <Input
                    value={otherDescription}
                    onChange={(e) => setOtherDescription(e.target.value)}
                    placeholder="Descreva o serviço adicional"
                    className="h-11 bg-white"
                    data-testid="other-description-main"
                  />
                </div>
              )}
            </div>
          </div>

          {/* ITEMS LIST */}
          <div className="flex-1 overflow-y-auto px-4 pt-3 pb-4">
            {currentRecord.items?.length > 0 && (
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">
                {currentRecord.items.length} artigo{currentRecord.items.length !== 1 ? 's' : ''} adicionado{currentRecord.items.length !== 1 ? 's' : ''}
              </p>
            )}

            {currentRecord.items?.length === 0 ? (
              <div className="text-center py-6 text-slate-400">
                <Scan className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm font-medium">Sem artigos de material</p>
                <p className="text-xs mt-1">Pode fechar o AP apenas com serviços</p>
              </div>
            ) : (
              <div className="space-y-2">
                {currentRecord.items.map((item) => (
                  <div
                    key={item.sku}
                    className="bg-white rounded-lg border border-slate-200 p-3"
                    data-testid={`item-card-${item.sku}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <p className="text-sm font-semibold leading-tight flex-1 min-w-0">{item.name}</p>
                      <p className="font-mono font-bold text-sm text-slate-900 flex-shrink-0">
                        €{((item.unit_price || 0) * item.quantity).toFixed(2)}
                      </p>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs text-slate-400 font-mono">{item.sku}</span>
                        <span className="text-xs text-slate-400 ml-2">€{(item.unit_price || 0).toFixed(2)}/un</span>
                      </div>
                      <div className="flex items-center gap-0">
                        <button
                          onClick={() => updateItemQuantity(item.sku, -1)}
                          className="h-10 w-10 flex items-center justify-center rounded-l-lg border border-slate-200 bg-slate-50 active:bg-red-50"
                          data-testid={`decrease-${item.sku}`}
                        >
                          {item.quantity === 1 ? <Trash2 className="h-3.5 w-3.5 text-red-500" /> : <Minus className="h-3.5 w-3.5 text-slate-600" />}
                        </button>
                        <div className="h-10 w-10 flex items-center justify-center border-y border-slate-200 bg-white font-bold text-base font-mono">
                          {item.quantity}
                        </div>
                        <button
                          onClick={() => updateItemQuantity(item.sku, 1)}
                          className="h-10 w-10 flex items-center justify-center rounded-r-lg border border-slate-200 bg-slate-50 active:bg-green-50"
                          data-testid={`increase-${item.sku}`}
                        >
                          <Plus className="h-3.5 w-3.5 text-slate-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* STICKY BOTTOM BAR */}
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]"
               data-testid="bottom-action-bar">
            <div className="flex items-center justify-between px-4 py-2 bg-slate-50/80 border-b border-slate-100">
              {(() => {
                const servicesTotal = (parseFloat(laborValue) || 0) + (parseFloat(travelValue) || 0) + (parseFloat(doorValue) || 0) + (parseFloat(otherValue) || 0);
                const itemsCount = currentRecord.items?.length || 0;
                const grandTotal = (currentRecord.total_value || 0) + servicesTotal;
                return (
                  <>
                    <span className="text-sm font-medium text-slate-600">
                      Total ({itemsCount} artigo{itemsCount !== 1 ? 's' : ''}{servicesTotal > 0 ? ' + serviços' : ''})
                    </span>
                    <span className="text-xl font-bold font-mono">€{grandTotal.toFixed(2)}</span>
                  </>
                );
              })()}
            </div>
            <div className="flex gap-3 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              <Button
                variant="outline"
                className="flex-1 h-14 text-base border-slate-300 active:bg-slate-100"
                onClick={handleCancelRecord}
                data-testid="cancel-record-btn"
              >
                <X className="h-5 w-5 mr-2" />
                Cancelar
              </Button>
              <Button
                className="flex-[2] h-14 text-base bg-green-600 hover:bg-green-700 active:bg-green-800"
                onClick={() => setShowCloseModal(true)}
                disabled={(() => {
                  // Allow closing if has items OR services (labor/travel/door/other)
                  const hasItems = currentRecord.items && currentRecord.items.length > 0;
                  const hasServices = (parseFloat(laborValue) || 0) > 0 || 
                                      (parseFloat(travelValue) || 0) > 0 || 
                                      (parseFloat(doorValue) || 0) > 0 || 
                                      (parseFloat(otherValue) || 0) > 0;
                  return !hasItems && !hasServices;
                })()}
                data-testid="close-record-btn"
              >
                <Check className="h-5 w-5 mr-2" />
                Fechar AP
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* CLOSE AP MODAL */}
      <Dialog open={showCloseModal} onOpenChange={setShowCloseModal}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md rounded-xl max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
          <DialogHeader className="px-4 pt-4 pb-3 sm:px-5 border-b border-slate-100 text-left space-y-1">
            <DialogTitle className="text-base sm:text-lg">Fechar AP {currentRecord?.ap_number}</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">Indique como foi pago este serviço</DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 sm:px-5">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-snug text-amber-900" data-testid="sage-close-hint">
            ⚠️ <strong>Preferencial:</strong> emite o documento no SAGE com <code className="font-mono">VossoNumero = {currentRecord?.ap_number || 'AP-XXXX'}</code> e o AP fecha sozinho. Usa este fecho manual só para APs não faturáveis.
          </div>
          <div className="space-y-5 py-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Método de Pagamento {paymentStatus !== 'paid' && <span className="text-slate-400 font-normal">(opcional)</span>}</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'MB', label: 'Cartão', icon: CreditCard },
                  { id: 'Numerário', label: 'Numerário', icon: Banknote },
                  { id: 'MBWay', label: 'MBWay', icon: Smartphone },
                  { id: 'Transferência', label: 'Transfer.', icon: ArrowRightLeft },
                ].map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    className={`h-16 flex flex-col items-center justify-center gap-1 rounded-lg border-2 transition-colors ${
                      paymentMethod === id
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-slate-200 bg-white text-slate-600 active:bg-slate-50'
                    }`}
                    onClick={() => setPaymentMethod(paymentMethod === id ? '' : id)}
                    data-testid={`payment-${id}`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="text-xs font-medium">{label}</span>
                  </button>
                ))}
              </div>
              {paymentMethod && (
                <button
                  onClick={() => setPaymentMethod('')}
                  className="text-xs text-slate-400 underline active:text-slate-600"
                  data-testid="clear-payment-btn"
                >
                  Limpar seleção
                </button>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Estado do Pagamento</label>
              <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                <SelectTrigger className="h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paid">Pago</SelectItem>
                  <SelectItem value="unpaid">Não Pago</SelectItem>
                  <SelectItem value="pending">Pendente</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Invoice intent — mandatory */}
            <div className="space-y-2 rounded-lg border-2 border-slate-200 p-3">
              <label className="text-sm font-medium">Cliente pretende fatura? <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  className={`h-12 rounded-lg border-2 font-semibold text-sm transition-colors ${
                    wantsInvoice === true ? 'border-red-500 bg-red-50 text-red-700' : 'border-slate-200 bg-white text-slate-600 active:bg-slate-50'
                  }`}
                  onClick={() => setWantsInvoice(true)}
                  data-testid="invoice-yes-btn"
                >
                  Sim
                </button>
                <button
                  className={`h-12 rounded-lg border-2 font-semibold text-sm transition-colors ${
                    wantsInvoice === false ? 'border-slate-700 bg-slate-100 text-slate-800' : 'border-slate-200 bg-white text-slate-600 active:bg-slate-50'
                  }`}
                  onClick={() => setWantsInvoice(false)}
                  data-testid="invoice-no-btn"
                >
                  Não
                </button>
              </div>
              {wantsInvoice === true && (
                <div className="space-y-2 pt-1">
                  <div className="space-y-1">
                    <label className="text-xs text-slate-600">NIF do cliente <span className="text-red-500">*</span></label>
                    <Input
                      inputMode="numeric"
                      maxLength={9}
                      value={clientNif}
                      onChange={(e) => setClientNif(e.target.value.replace(/\D/g, '').slice(0, 9))}
                      placeholder="123456789"
                      className="h-12 text-base font-mono"
                      data-testid="client-nif-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-slate-600">Nome para a fatura <span className="text-red-500">*</span></label>
                    <Input
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="Nome do cliente / empresa"
                      className="h-12 text-base"
                      data-testid="client-name-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-slate-600">Email para envio da fatura <span className="text-red-500">*</span></label>
                    <Input
                      type="email"
                      inputMode="email"
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      placeholder="cliente@email.pt"
                      className="h-12 text-base"
                      data-testid="client-email-input"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Summary of charges - read-only view */}
            {currentRecord && (() => {
              const itemsTotal = (currentRecord.items || []).reduce(
                (sum, it) => sum + (it.unit_price || 0) * (it.quantity || 0), 0,
              );
              const labor = parseFloat(laborValue) || 0;
              const travel = parseFloat(travelValue) || 0;
              const door = parseFloat(doorValue) || 0;
              const other = parseFloat(otherValue) || 0;
              const finalTotal = itemsTotal + labor + travel + door + other;
              return (
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Itens ({currentRecord.items?.length || 0})</span>
                    <span className="font-mono">€{itemsTotal.toFixed(2)}</span>
                  </div>
                  {labor > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Mão de Obra</span>
                      <span className="font-mono">€{labor.toFixed(2)}</span>
                    </div>
                  )}
                  {travel > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Deslocação</span>
                      <span className="font-mono">€{travel.toFixed(2)}</span>
                    </div>
                  )}
                  {door > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Abertura de porta</span>
                      <span className="font-mono">€{door.toFixed(2)}</span>
                    </div>
                  )}
                  {other > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500 truncate max-w-[60%]" title={otherDescription}>
                        Outros{otherDescription ? `: ${otherDescription}` : ''}
                      </span>
                      <span className="font-mono">€{other.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between mt-2 pt-2 border-t text-lg">
                    <span className="font-semibold">Valor Total</span>
                    <span className="font-bold font-mono" data-testid="close-modal-total">€{finalTotal.toFixed(2)}</span>
                  </div>
                </div>
              );
            })()}
          </div>
          </div>
          <DialogFooter className="px-4 py-3 sm:px-5 border-t border-slate-100 bg-white flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" className="h-12 w-full sm:w-auto" onClick={() => setShowCloseModal(false)}>Cancelar</Button>
            <Button
              onClick={handleCloseRecord}
              disabled={loading || wantsInvoice === null || (paymentStatus === 'paid' && !paymentMethod)}
              className="h-12 w-full sm:w-auto bg-green-600 hover:bg-green-700 active:bg-green-800"
              data-testid="confirm-close-btn"
            >
              {loading ? <RefreshCw className="h-5 w-5 animate-spin mr-2" /> : <Check className="h-5 w-5 mr-2" />}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

